import { app } from 'app/app';
import { AppModule } from 'app/module';
import { KEYCODE_TABULATOR } from 'app/keyCodes';
import { Input } from 'module/input/main';
import { Autocomplete } from 'module/autocomplete/main';
import { Checkbox } from 'module/checkbox/main';
import { JobsFilterDropdown } from 'module/jobsFilterDropdown/main';
import { Dropdown } from 'module/dropdown/main';
import { RadioGroup } from 'module/radioGroup/main';
import { ResultState } from 'module/resultState/main';
import {
    API_ENDPOINT_AUTOCOMPLETE_KEYWORDS,
    API_ENDPOINT_AUTOCOMPLETE_LOCATIONS, API_ENDPOINT_JOBALARM_DELETE,
    API_ENDPOINT_PROXIMITY_LOCATION,
} from 'app/endpoints';
import { mediaQueries } from 'util/mediaQueries';
import { MAIL_DAILY, MAIL_WEEKLY } from 'app/mailSubscriptionInterval';
import { deleteJson, getJson, postJson } from 'util/xhr';
import { serializeJson } from 'util/form';
import { trackGa4 } from 'util/googleAnalytics';
import { ValidationBoxBase } from 'module/validationBox/main';
import deepEqual from 'util/deepEqual';
import * as jobAlarmResultState from 'util/jobAlarmResultStates';
import 'module/spot/styles.scss';
import './styles.scss';
import { user } from 'app/user';
import Template from './template.hbs';

const defaultSalaryId = '10006';
let oldFormData = false;

export class FormModal extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    ready() {
        app.events.on('onWindowResize', () => this.onWindowResize());
    }

    afterRender() {
        oldFormData = serializeJson(this.dom.form);
        oldFormData.mailNotificationInterval = parseInt(this.mailNotificationSwitch.getValue(), 10);
    }

    domBindings() {
        this.dom.form = this.dom.el.querySelector('.m-formModal__contentInner');
        this.dom.formOverlay = this.dom.el.querySelector('.m-formModal__overlay');
        this.dom.headline = this.dom.el.querySelector('.m-formModal__headline');
        this.dom.subHeadline = this.dom.el.querySelector('.m-formModal__subHeadline');
        this.dom.inputs = this.dom.el.querySelectorAll('.m-input');
        this.dom.dropdowns = this.dom.el.querySelectorAll('.m-formModal__dropdown');
        this.dom.submitButton = this.dom.el.querySelector('.m-formModal__submitButton');
        this.dom.jobsFilterDropdown = this.dom.el.querySelectorAll('.m-jobsFilterDropdown');
        this.dom.jobsFilterCheckboxes = this.dom.el.querySelectorAll('.m-formModal__checkbox .m-checkbox');
        this.dom.email = this.dom.el.querySelector('.m-formModal__email');
        this.dom.closeTrigger = this.dom.el.querySelector('.m-formModal__closeTrigger');
        this.dom.resultState = this.dom.el.querySelector('.m-resultState__resultContainer');
        this.dom.errorBox = this.dom.el.querySelector('.m-formModal__error .m-validationBoxBase');
        this.dom.mailNotificationSwitchContainer = this.dom.el.querySelector('.m-formModal__mailNotificationSwitch');
        this.dom.mailNotificationSwitch = this.dom.el
            .querySelector('.m-formModal__mailNotificationSwitch .m-radioGroup');
        this.dom.deleteButton = this.dom.el.querySelector('.m-formModal__deleteButton');

        const generatedProps = {
            inputs: this.props.inputs ?? [],
            dropdowns: this.props.dropdowns ?? [],
        };

        this.inputs = [];
        this.dropdowns = [];
        this.inputAutocompletes = [];
        this.jobsFilterDropdown = [];
        this.jobsFilterCheckboxes = [];

        for (let i = 0; i < this.dom.inputs.length; i += 1) {
            const autocomplete = this.dom.inputs[i].parentNode.querySelector('.m-autocomplete');
            const dropdownContainer = this.dom.inputs[i].parentNode.nextElementSibling;
            this.inputs[i] = new Input(this.dom.inputs[i]);
            generatedProps.inputs[i] = {
                input: {},
            };
            generatedProps.inputs[i].input = this.inputs[i].getPropsFromDom();
            if (autocomplete) {
                this.inputAutocompletes.push(new Autocomplete(autocomplete));
                this.inputAutocompletes[this.inputAutocompletes.length - 1].set({
                    input: this.inputs[i],
                    endpoint: this.inputs[i].dom.input.name === 'keywords'
                        ? API_ENDPOINT_AUTOCOMPLETE_KEYWORDS
                        : API_ENDPOINT_AUTOCOMPLETE_LOCATIONS,
                });
            }
            if (dropdownContainer && dropdownContainer.classList.contains('m-formModal__dropdown')) {
                this.dropdowns.push(new Dropdown(dropdownContainer.querySelector('.m-dropdown')));
                generatedProps.inputs[i].input.select = this.dropdowns[this.dropdowns.length - 1].getPropsFromDom();
            }
        }

        if (generatedProps.dropdowns.length === 0) {
            [].forEach.call(this.dom.jobsFilterDropdown, ($item) => {
                this.jobsFilterDropdown.push(new JobsFilterDropdown($item));
                generatedProps.dropdowns.push(
                    this.jobsFilterDropdown[this.jobsFilterDropdown.length - 1].getPropsFromDom(),
                );
            });

            [].forEach.call(this.dom.jobsFilterCheckboxes, ($item) => {
                this.jobsFilterCheckboxes.push(new Checkbox($item));
                generatedProps.dropdowns.push({ checkbox: true,
                    ...this.jobsFilterCheckboxes[this.jobsFilterCheckboxes.length - 1].getPropsFromDom() });
            });
        } else {
            [].forEach.call(this.dom.jobsFilterDropdown, ($item) => {
                this.jobsFilterDropdown.push(new JobsFilterDropdown($item));
            });

            [].forEach.call(this.dom.jobsFilterCheckboxes, ($item) => {
                this.jobsFilterCheckboxes.push(new Checkbox($item));
            });
        }

        if (this.dom.mailNotificationSwitch) {
            this.mailNotificationSwitch = new RadioGroup(this.dom.mailNotificationSwitch);
        }

        this.props = Object.assign(this.props, generatedProps);
    }

    subs() {
        if (this.dom.resultState) {
            this.resultState = new ResultState(this.dom.resultState);
        }

        if (this.dom.errorBox) {
            this.errorBox = new ValidationBoxBase(this.dom.errorBox);
        }
    }

    subEvents() {
        for (let i = 0; i < this.inputs.length; i += 1) {
            if (this.inputs[i].props.hasSelect && this.inputs[i].props.hasAutocomplete) {
                for (let j = 0; j < this.inputAutocompletes.length; j += 1) {
                    if (this.inputAutocompletes[j].input.props.id === this.inputs[i].props.id) {
                        for (let l = 0; l < this.dropdowns.length; l += 1) {
                            this.dropdowns[l].events.on('closeAllDropdowns', () => this.closeAllDropdowns());
                            if (this.dropdowns[l].props.id === this.props.inputs[i].input.select.id) {
                                // eslint-disable-next-line max-len
                                this.inputs[i].events.on(
                                    'actionIconClicked',
                                    (e) => this.resetSearchInput(e, this.dropdowns[l], this.inputs[i]),
                                );
                                // eslint-disable-next-line max-len
                                this.inputAutocompletes[j].events.on('changed', (e) => this.checkProximityStatus(e, this.dropdowns[l]));
                                // eslint-disable-next-line max-len
                                this.inputAutocompletes[j].events.on('hide', () => this.resetProximityDropdown(this.dropdowns[l]));
                            }
                        }
                    }
                }
            } else {
                this.inputs[i].events.on(
                    'actionIconClicked',
                    (e) => this.resetSearchInput(e, false, this.inputs[i]),
                );
            }
        }
        if (this.jobsFilterDropdown) {
            this.jobsFilterDropdown.forEach((filterDropdown) => {
                filterDropdown.events.on('updateFilterProps', () => {
                    this.updateSubmitButtonStatus();
                });
            });
        }
        if (this.dropdowns) {
            this.dropdowns.forEach((dropdown) => {
                dropdown.events.on('change', () => {
                    this.updateSubmitButtonStatus();
                });
            });
        }
        if (this.inputs) {
            this.inputs.forEach((input) => {
                input.events.on('change', () => {
                    this.updateSubmitButtonStatus();
                });
            });
        }
        if (this.inputAutocompletes) {
            this.inputAutocompletes.forEach((autocomplete) => {
                autocomplete.events.on('autoCompleteItemClicked', () => {
                    this.updateSubmitButtonStatus();
                });
            });
        }
        if (this.jobsFilterCheckboxes) {
            this.jobsFilterCheckboxes.forEach((checkbox) => {
                checkbox.events.on('toggle', () => {
                    this.updateSubmitButtonStatus();
                });
            });
        }
        if (this.mailNotificationSwitch) {
            this.mailNotificationSwitch.events.on('change', () => {
                this.updateSubmitButtonStatus();
            });
        }
    }

    domEvents() {
        if (this.dom.form) {
            this.dom.form.addEventListener('submit', (e) => this.submit(e));
        }
        if (this.dom.closeTrigger) {
            this.dom.closeTrigger.addEventListener('click', (e) => {
                if (e) e.preventDefault();
                this.closeModalViaClick();
            });
        }
        if (this.dom.formOverlay) {
            this.dom.formOverlay.addEventListener('click', () => this.closeModalViaClick());
        }
        if (this.dom.deleteButton) {
            this.dom.deleteButton.addEventListener('click', (e) => this.deleteJobAlarm(e));
        }

        this.dom.el.addEventListener('keydown', (e) => this.pressKey(e, e.key));
    }

    getPropsFromDom() {
        const props = {
            visible: !this.dom.el.classList.contains('m-formModal--hide'),
            headline: this.dom.headline.innerText,
            subHeadline: this.dom.subHeadline ? this.dom.subHeadline.innerText : '',
            submitButtonText: this.dom.submitButton ? this.dom.submitButton.innerText : '',
            action: this.dom.form.action,
            formEvent: this.dom.form.getAttribute('data-form-event-action'),
            email: this.dom.email.value,
            hideAggregations: this.dom.el.getAttribute('data-hide-aggregations') === 'true',
            jobAlarmId: this.dom.form.getAttribute('data-jobalarm-id'),
            mailNotificationSwitch: this.mailNotificationSwitch?.getPropsFromDom(),
        };

        return Object.assign(this.props, props);
    }

    pressKey(e, keyCode) {
        if (keyCode === KEYCODE_TABULATOR && document.activeElement === this.dom.deleteButton && e.shiftKey === false) {
            e.preventDefault();
            this.dom.closeTrigger.focus();
        } else if (
            keyCode === KEYCODE_TABULATOR && document.activeElement === this.dom.closeTrigger && e.shiftKey === true
        ) {
            e.preventDefault();
            this.dom.deleteButton.focus();
        }
    }

    submit(e) {
        e.preventDefault();

        const hasActiveAutocomplete = this.hasActiveAutocomplete();

        if (!hasActiveAutocomplete) {
            const data = this.prepareFormData(serializeJson(this.dom.form));
            if (data.salary === defaultSalaryId) {
                data.salary = '';
            }
            if (this.mailNotificationSwitch) {
                data.mailNotificationInterval = parseInt(this.mailNotificationSwitch.getValue(), 10);
            }
            if (this.validate()) {
                this.submitForm(data);
            }
        }
    }

    prepareFormData(querys) {
        Object.keys(querys).forEach((query) => {
            if (Array.isArray(querys[query])) {
                querys[query.substring(0, query.length - 2)] = querys[query].join();
                delete querys[query];
            }
        });
        return querys;
    }

    validationRules() {
        this.validation.addRule(this, {
            name: 'jobalarm_empty',
            message: 'Fülle mindestens ein Feld aus.',
            test: () => this.validateValues(),
            validationMessageOutlet: this.errorBox,
        });
    }

    validateValues() {
        const data = this.prepareFormData(serializeJson(this.dom.form));
        if (data.salary === defaultSalaryId) {
            data.salary = '';
        }
        let tempData = { ...data };
        let empty = true;
        delete tempData.email;
        // eslint-disable-next-line no-underscore-dangle
        delete tempData._token;

        tempData = Object.values(tempData);

        tempData.forEach((d) => {
            if (d !== '' && d !== '0' && d !== 0) {
                empty = false;
            }
        });
        return !empty;
    }

    submitForm(data) {
        if (this.request) {
            this.request.abort();
        }

        this.dom.submitButton.disabled = true;

        const jobSearchRequest = this.prepareJobSearchRequest();

        this.request = postJson(
            this.dom.form.action,
            { data },
            (response) => this.onResponse(response, jobSearchRequest),
            (error) => this.onResponseError(error),
        );
    }

    onResponse(response, jobSearchRequest) {
        let snackbarText;
        let snackbarLink = '/settings/notifications#c-notificationIndex-jobalarm';
        let snackbarLinkText = 'Anpassen';
        const actualJobAlarmDataChanged = this.actualJobAlarmDataChanged();

        this.updateProps({ visible: false, errors: false }, true);
        this.events.emit('removeModalStyle');
        const isUpdateAction = response.isUpdateAction !== undefined ? response.isUpdateAction : false;
        const jobAlarmId = response.jobAlarm !== undefined ? response.jobAlarm.id : false;

        this.events.emit(
            'changedJobalarm',
            { jobalarmId: jobAlarmId },
            isUpdateAction,
            jobSearchRequest,
            response.mailNotificationInterval,
            actualJobAlarmDataChanged,
        );

        if (parseInt(response.mailNotificationInterval, 10) === MAIL_DAILY) {
            snackbarText = 'Ab sofort erhältst du täglich neue Jobs per E-Mail.';
        } else if (parseInt(response.mailNotificationInterval, 10) === MAIL_WEEKLY) {
            snackbarText = 'Ab sofort erhältst du wöchentlich neue Jobs per E-Mail.';
        } else {
            // If user selects "Nie" or if JA edit modal is submitted
            // (mailNotificationInterval flag is not sent to FE in this case), show generic snackbar
            snackbarLink = false;
            snackbarLinkText = false;
        }

        this.events.emit(
            'showSnackbar',
            snackbarText,
            snackbarLink,
            snackbarLinkText,
        );

        if (!isUpdateAction) {
            trackGa4({
                event: 'jobalarm_create_success',
                method: 'Job Alarm Modal',
                user_data: {
                    email: user.getUserMail(),
                },
            });
        }
    }

    onResponseError(response) {
        this.dom.submitButton.disabled = false;
        if (!response || !response.errors) return false;

        const errorsMeta = this.validation.handleBackendValidationErrors(response);

        let resultState;
        if (errorsMeta.general?.[0]?.jobalarm_exists) {
            resultState = jobAlarmResultState.alreadyExists();
        } else if (errorsMeta.general?.[0]?.jobalarm_limit_exceeded) {
            resultState = jobAlarmResultState.limitExceeded();
        } else {
            resultState = jobAlarmResultState.generalError();
        }

        this.updateProps({
            resultState,
        }, true);

        return true;
    }

    checkProximityStatus(e, dropdown) {
        const query = e.arguments.length > 0 ? e.arguments[0] : null;

        if (query === null) {
            return;
        }

        if (this.proximityRequest) {
            this.proximityRequest.abort();
        }

        const encodedQuery = encodeURIComponent(query);
        const url = `${API_ENDPOINT_PROXIMITY_LOCATION}?query=${encodedQuery}`;

        this.proximityRequest = getJson(
            url,
            {},
            (response) => this.onProximityResponse(response, dropdown),
            (error) => this.onProximityError(error, dropdown),
        );
    }

    onProximityResponse(response, dropdown) {
        const { isProximityDisabled } = response;

        if (isProximityDisabled) {
            dropdown.resetOptions(isProximityDisabled);
        } else {
            dropdown.enableDropdown();
        }
    }

    onProximityError(error, dropdown) {
        dropdown.resetOptions(true);
    }

    resetSearchInput(e, dropdown, inputField) {
        if (inputField) inputField.resetValue();
        if (dropdown && !dropdown.props.disabled) {
            dropdown.resetOptions(true);
        }
        this.updateSubmitButtonStatus();
    }

    resetProximityDropdown(dropdown) {
        if (!dropdown.props.disabled) {
            dropdown.resetOptions(true);
        }
    }

    hasActiveAutocomplete() {
        for (let i = 0; i < this.inputAutocompletes.length; i += 1) {
            const props = this.inputAutocompletes[i].getProps();
            if (props.selectedIndex) {
                this.inputAutocompletes[i].insertSelectedItem();
                return true;
            }
        }
        return false;
    }

    closeModal() {
        this.updateProps({ visible: false, errors: false, resultState: null }, true);
        this.events.emit('removeModalStyle');
    }

    closeModalViaClick() {
        if (mediaQueries.isMobile()) {
            window.history.back();
        } else {
            this.closeModal();
        }
    }

    onWindowResize() {
        if (this.props.visible) {
            this.events.emit('removeModalStyle');
        }
    }

    openModalViaClick() {
        this.updateProps({ visible: true }, true);
        this.dom.closeTrigger.focus();
        this.events.emit('addModalStyle');
        if (mediaQueries.isMobile()) {
            this.events.emit('pushStateFormModal');
        }
    }

    openModal() {
        this.updateProps({ visible: true }, true);
        this.dom.closeTrigger.focus();
        this.events.emit('addModalStyle');
    }

    openModalWithData(data) {
        const viewdata = { visible: true, ...data };
        this.updateProps(viewdata, true);
        this.dom.closeTrigger.focus();
        this.updateSubmitButtonStatus();
        this.events.emit('addModalStyle');

        if (mediaQueries.isMobile()) {
            this.events.emit('pushStateFormModal');
        }
    }

    closeAllDropdowns() {
        if (this.dropdowns) {
            for (let i = 0; i < this.dropdowns.length; i += 1) {
                this.dropdowns[i].closeDropdown();
            }
        }
    }

    isOpen() {
        return this.props.visible;
    }

    updateSubmitButtonStatus() {
        const newFormData = serializeJson(this.dom.form);
        newFormData.mailNotificationInterval = parseInt(this.mailNotificationSwitch.getValue(), 10);
        this.dom.submitButton.disabled = deepEqual(oldFormData, newFormData);
    }

    actualJobAlarmDataChanged() {
        const newFormData = serializeJson(this.dom.form);
        delete newFormData.mailNotificationInterval;
        delete oldFormData.mailNotificationInterval;

        return !deepEqual(oldFormData, newFormData);
    }

    deleteJobAlarm(e) {
        e.preventDefault();

        if (this.request) {
            this.request.abort();
        }

        const data = serializeJson(this.dom.form);
        const url = `${API_ENDPOINT_JOBALARM_DELETE}/${this.props.jobAlarmId}`;
        this.request = deleteJson(
            url,
            { data },
            (response) => this.onDeleteResponse(response),
            () => this.onDeleteResponseError(),
        );
    }

    onDeleteResponse() {
        this.closeModalViaClick();
        setTimeout(() => {
            this.events.emit('jobalarmDeleted', { jobalarmId: this.props.jobAlarmId });
        }, 100);
        this.events.emit('showSnackbar', 'Jobalarm wurde gelöscht.');
    }

    onDeleteResponseError() {
        this.closeModalViaClick();
    }

    prepareJobSearchRequest() {
        const newProps = Object.assign(
            serializeJson(this.dom.form),
            // always send empty slugs from search form
            {
                keywordSlug: false,
                locationSlug: false,
            },
        );
        if (newProps['salary[]'][0] === defaultSalaryId) {
            newProps['salary[]'].splice(0, newProps['salary[]'].length);
        }

        delete newProps.mailNotificationIntervalEditForm;
        delete newProps.email;

        return newProps;
    }
}
